import React, { createContext, useContext, useState, ReactNode } from "react";
import ReactDOM from "react-dom";
import PolicyModals from "../pages/Main/Policy";

interface FooterModalContextType {
  showPrivacyModal: boolean;
  showTermsModal: boolean;
  showProfessionalTermsModal: boolean;
  setShowPrivacyModal: (show: boolean) => void;
  setShowTermsModal: (show: boolean) => void;
  setShowProfessionalTermsModal: (show: boolean) => void;
  isUsingProvider?: boolean;
}

const FooterModalContext = createContext<FooterModalContextType | undefined>(undefined);

export const useFooterModals = () => {
  const context = useContext(FooterModalContext);
  
  // Fallback state - always initialize (hooks must be called unconditionally)
  const [localShowPrivacyModal, setLocalShowPrivacyModal] = useState(false);
  const [localShowTermsModal, setLocalShowTermsModal] = useState(false);
  const [localShowProfessionalTermsModal, setLocalShowProfessionalTermsModal] = useState(false);
  
  // If no provider, use local state as fallback
  if (!context) {
    console.warn("⚠️ [FooterModalContext] useFooterModals used without provider - using local state fallback");
    return {
      showPrivacyModal: localShowPrivacyModal,
      showTermsModal: localShowTermsModal,
      showProfessionalTermsModal: localShowProfessionalTermsModal,
      setShowPrivacyModal: setLocalShowPrivacyModal,
      setShowTermsModal: setLocalShowTermsModal,
      setShowProfessionalTermsModal: setLocalShowProfessionalTermsModal,
      isUsingProvider: false,
    };
  }
  
  // Otherwise use context
  return { ...context, isUsingProvider: true };
};

export const FooterModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showPrivacyModal, _setShowPrivacyModal] = useState(false);
  const [showTermsModal, _setShowTermsModal] = useState(false);
  const [showProfessionalTermsModal, _setShowProfessionalTermsModal] = useState(false);

  // Wrapped setters with logging
  const setShowPrivacyModal = (value: boolean) => {
    console.log("🟣 [FooterModalContext] setShowPrivacyModal called with value:", value);
    _setShowPrivacyModal(value);
  };

  const setShowTermsModal = (value: boolean) => {
    console.log("🟣 [FooterModalContext] setShowTermsModal called with value:", value);
    _setShowTermsModal(value);
  };

  const setShowProfessionalTermsModal = (value: boolean) => {
    console.log("🟣 [FooterModalContext] setShowProfessionalTermsModal called with value:", value);
    _setShowProfessionalTermsModal(value);
  };

  return (
    <FooterModalContext.Provider
      value={{
        showPrivacyModal,
        showTermsModal,
        showProfessionalTermsModal,
        setShowPrivacyModal,
        setShowTermsModal,
        setShowProfessionalTermsModal,
      }}
    >
      {children}
      {/* Render modals at provider level - they will persist */}
      {typeof document !== "undefined" && ReactDOM.createPortal(
        <PolicyModals
          showPrivacyModal={showPrivacyModal}
          showTermsModal={showTermsModal}
          showProfessionalTermsModal={showProfessionalTermsModal}
          onClosePrivacy={() => {
            console.log("🟣 [FooterModalContext] Closing Privacy Modal");
            setShowPrivacyModal(false);
          }}
          onCloseTerms={() => {
            console.log("🟣 [FooterModalContext] Closing Terms Modal");
            setShowTermsModal(false);
          }}
          onCloseProfessionalTerms={() => {
            console.log("🟣 [FooterModalContext] Closing Professional Terms Modal");
            setShowProfessionalTermsModal(false);
          }}
        />,
        document.body
      )}
    </FooterModalContext.Provider>
  );
};

